import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconImage } from '@prisjakt/ui/icon/image';
import { IconPlayCircleOutlined } from '@prisjakt/ui/icon/play-circle-outlined';

import { ProductHeaderProductOfTheYearAwardBadge } from 'src/components/product-of-the-year/badges';
import { useIsProductOfTheYear } from 'src/components/product-of-the-year/hooks';
import { useProductContext } from '../product-context';
import ProductMediaLightbox from '../product-media-lightbox';
import MediaCounter from './media-counter';

import { productMediaOpen } from '../../actions/media';

import { trackProductMediaOpened } from './tracking';

import type { Media } from './types';

import { StyledCounters, StyledFirstImage, StyledProductMedia } from './styles';
import { useProductMedia } from './use-product-media';
import type { ProductVideo } from 'src/types';

type Props = {
  category?: {
    logo?: string | null;
  } | null;
  className?: string;
  name: string;
  id: number;
};

export const ProductMedia = (props: Props) => {
  const { id, category, className, name } = props;
  const { data } = useProductMedia({ id });

  const [show, setShow] = useState<boolean>(false);

  const dispatch = useDispatch();

  const media = data?.product?.media;
  // Filters out null values in arrays
  const images = media?.images?.filter(item => item) as string[] | undefined;
  const large = media?.large?.filter(item => item) as string[] | undefined;
  const videos = media?.videos?.filter(item => item) as
    | ProductVideo[]
    | undefined;

  const hasLightboxContent = () => {
    return Boolean(large?.length || videos?.length);
  };

  const open = () => {
    if (!hasLightboxContent()) {
      return;
    }

    trackProductMediaOpened();

    setShow(true);
    dispatch(productMediaOpen());
  };

  const close = () => setShow(false);

  const { product } = useProductContext();
  const productOfTheYearAward = useIsProductOfTheYear(product.id);

  if (!media) {
    return null;
  }

  return (
    <>
      {show && <ProductMediaLightbox media={media as Media} close={close} />}
      <StyledProductMedia
        className={className}
        onClick={open}
        data-test="ProductMedia"
        data-test-has-lightbox-content={hasLightboxContent()}
        $isFallbackImage={!media?.first}
        $more={Boolean(images)}
      >
        {productOfTheYearAward && (
          <ProductHeaderProductOfTheYearAwardBadge
            badgeUrl={productOfTheYearAward.awardLogo}
            awardName={productOfTheYearAward.awardName}
            year={productOfTheYearAward.year}
          />
        )}
        <StyledFirstImage>
          <img src={media.first || category?.logo || undefined} alt={name} />
        </StyledFirstImage>
        {(images || videos) && (
          <StyledCounters>
            <MediaCounter icon={IconImage} mediaList={images} />
            <MediaCounter icon={IconPlayCircleOutlined} mediaList={videos} />
          </StyledCounters>
        )}
      </StyledProductMedia>
    </>
  );
};
